import { MessageCircle, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { useSmsHref } from "@/components/site/ContactButtons";
import { SMS_DRAFTS } from "@/lib/contact";

const faqs = [
  {
    id: "location",
    question: "Where are you located?",
    answer:
      "We're in Villa Rica, Georgia, about 35 miles west of Atlanta. Pieces may be available to view in Villa Rica or at Red Door Antiques in LaGrange. Contact Chris to confirm the location and availability of the piece you want to see, and please call ahead before visiting.",
  },
  {
    id: "pricing",
    question: "What do pieces cost?",
    answer:
      "Small statues start around $10-15, bird baths start at $60, and working two-tier fountains start at $450. Check the Available Pieces page for current prices, or call or message for an exact quote.",
  },
  {
    id: "delivery",
    question: "Do you deliver nationwide?",
    answer:
      "Yes. Delivery is available throughout the United States for a small fee. Contact Chris with your location and the piece you’re interested in for a delivery quote.",
  },
  {
    id: "custom",
    question: "Can I order a custom piece?",
    answer:
      "Absolutely — that's half of what we do. Chris works with you on size, finish, style, and placement, and can even color match a finish to your home or existing stonework. Call or send a message to start the conversation.",
  },
  {
    id: "availability",
    question: "How do I know if a piece is still available?",
    answer:
      "Pieces come and go faster than the site can keep up. Please call or text Chris at (678) 769-9652 to confirm a piece is still available, and where to see it, before you make the drive.",
  },
  {
    id: "installation",
    question: "I bought a fountain somewhere else. Can Chris install it?",
    answer:
      "Yes. Chris installs fountains purchased elsewhere. Call Chris at (678) 769-9652 to discuss your project, or text photos of the fountain and the space where you'd like it installed. Scope, location, and the quote are confirmed directly with Chris.",
  },
  {
    id: "pump",
    question: "Does a fountain come with the pump?",
    answer:
      "Yes. Fountains include a working pump, and when Chris delivers he sets the piece, levels it, and leaves it running. All you need is a standard outdoor outlet nearby.",
  },
];

export default function Faq() {
  const generalSms = useSmsHref(SMS_DRAFTS.general);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="bg-[#EFEBE1] py-10 md:py-14 border-t border-[#D5CEBD]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="section-eyebrow mb-5">
              <span>Good To Know</span>
            </div>
            <h2
              data-testid="faq-heading"
              className="font-serif font-light text-[#2C2A28] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6"
            >
              Questions folks ask us.
            </h2>
            <p className="text-[#5C5955] text-base md:text-lg leading-relaxed max-w-md">
              Don&rsquo;t see your question here? Call or send a message —
              Chris is happy to talk it through.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md">
              <a
                href="tel:+16787699652"
                data-testid="faq-call"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#7A6547] text-[#F9F6F0] text-[11px] tracking-[0.2em] uppercase hover:bg-[#5E4D35] transition-colors"
              >
                <Phone size={13} strokeWidth={1.5} />
                (678) 769-9652
              </a>
              <a
                href={generalSms}
                data-testid="faq-messenger"
                aria-label="Text Chris at (678) 769-9652"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#7A6547] text-[#7A6547] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A6547] hover:text-[#F9F6F0] transition-colors"
              >
                <MessageCircle size={13} strokeWidth={1.5} />
                Text Chris
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {faqs.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="border-b border-[#D5CEBD]"
                >
                  <AccordionTrigger
                    data-testid={`faq-question-${faq.id}`}
                    className="text-left font-serif text-lg md:text-xl text-[#2C2A28] font-normal hover:no-underline hover:text-[#7A6547] py-6"
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${faq.id}`}
                    className="text-[#5C5955] text-base leading-relaxed pb-6"
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}
